/**
 * Storage class to extend from.
 *
 * Usage.
 *
 * Sores data against a key for latter use.
 * this.set( key, data );
 *
 * Retrieves stored data by key OR key and subKey.
 * this.get( key );
 *
 * Also can retrieve keys or data by first, last or given index.
 */
export class Store {
	_keys = [];

	_data = [];

	/**
	 * Set data for local storage against the supplied key.
	 *
	 * @param {number|string} key
	 * @param {*} data
	 */
	set( key, data ) {
		const dataToSave = {
			...data,
		};

		const index = this.keys.indexOf( key );
		if ( index !== -1 ) {
			this.data[ index ] = dataToSave;
		} else {
			this.keys = key;
			this.data = dataToSave;
		}
	}

	/**
	 * Get data from local storage by the given key.
	 *
	 * @param {string} key
	 * @param {*} subKey
	 *
	 * return {*}
	 */
	get( key, subKey = null ) {
		let data = this.data[ this.keys.indexOf( key ) ];
		if ( null !== subKey ) {
			data = data[ subKey ];
		}
		return data;
	}

	/**
	 * Return the first key added or null.
	 *
	 * return {string}
	 */
	keysFirst() {
		return this.keys.length ? this.keys[ 0 ] : null;
	}

	/**
	 * Return the last key added or null.
	 *
	 * return {string}
	 */
	keysLast() {
		return this.keys.length ? this.keys[ this.keys.length - 1 ] : null;
	}

	/**
	 * Return the key found at the given index or null.
	 *
	 * @param {number} index
	 *
	 * return {string}
	 */
	keysIndex( index ) {
		return this.keys.length && this.keys[ index ] ? this.keys[ index ] : null;
	}

	/**
	 * Return the first data added or null.
	 *
	 * return {*}
	 */
	dataFirst() {
		return this.data.length ? this.data[ 0 ] : null;
	}

	/**
	 * Return the last data added or null.
	 *
	 * return {*}
	 */
	dataLast() {
		return this.data.length ? this.data[ this.data.length - 1 ] : null;
	}

	/**
	 * Return the data found at the given index or null.
	 *
	 * @param {number} index
	 *
	 * return {*}
	 */
	dataIndex( index ) {
		return this.data.length && this.data[ index ] ? this.data[ index ] : null;
	}

	/**
	 * Given a piece of data search in the stored objects for the matching index within the main data object.
	 *
	 * @param data
	 * @returns {boolean}
	 */
	indexOfData( data ) {
		let index = false;
		if ( typeof data === 'string' || data instanceof String ) {
			let found = false;
			for ( let i = 0; i < this.data.length; i++ ) {
				if ( this.data[ i ] && typeof this.data[ i ] === 'object' && this.data[ i ].constructor === Object ) {
					const obj = this.data[ i ];
					found = Object.values( obj ).indexOf( data ) > -1;
				}

				if ( found ) {
					index = i;
					break;
				}
			}
		}

		return index;
	}

	/** ------------------------------------------------------------------------------------------
	 * Setters.
	 * ------------------------------------------------------------------------------------------ */

	set keys( value ) {
		this.keys.push( value );
	}

	set data( value ) {
		this.data.push( value );
	}

	/** ------------------------------------------------------------------------------------------
	 * Getters.
	 * ------------------------------------------------------------------------------------------ */

	get keys() {
		return this._keys;
	}

	get data() {
		return this._data;
	}

}
